import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import {
  Camera, Sun, Moon, Video, VideoOff, Compass,
  RotateCcw, Crosshair, AlertTriangle, Smartphone
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const ARCameraView = ({ latitude, longitude, sunTimes }) => {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [deviceOrientation, setDeviceOrientation] = useState({ alpha: 0, beta: 0, gamma: 0 });
  const [compassHeading, setCompassHeading] = useState(0);
  const [hasOrientationPermission, setHasOrientationPermission] = useState(false);
  const [manualHeading, setManualHeading] = useState(0);
  const [useManualHeading, setUseManualHeading] = useState(false);
  const [timeOffset, setTimeOffset] = useState(0); // Hours offset from now
  const animationRef = useRef(null);

  // Calculate sun position
  const calculateSunPosition = useCallback((date) => {
    const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000);
    const hour = date.getHours() + date.getMinutes() / 60;
    
    const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);
    const solarNoon = 12.0 - longitude / 15.0;
    const hourAngle = 15 * (hour - solarNoon);
    
    const latRad = latitude * Math.PI / 180;
    const decRad = declination * Math.PI / 180;
    const haRad = hourAngle * Math.PI / 180;
    
    const sinAlt = Math.sin(latRad) * Math.sin(decRad) + 
                   Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
    const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180 / Math.PI;
    
    const cosAz = (Math.sin(decRad) - Math.sin(latRad) * Math.sin(altitude * Math.PI / 180)) / 
                  (Math.cos(latRad) * Math.cos(altitude * Math.PI / 180) + 0.0001);
    let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
    if (hourAngle > 0) azimuth = 360 - azimuth;
    
    return { azimuth, altitude };
  }, [latitude, longitude]);

  // Calculate moon position (simplified)
  const calculateMoonPosition = useCallback((date) => {
    const daysSinceJ2000 = (date - new Date(2000, 0, 1, 12, 0, 0)) / 86400000;
    const L = (218.316 + 13.176396 * daysSinceJ2000) % 360;
    const M = (134.963 + 13.064993 * daysSinceJ2000) % 360;
    const F = (93.272 + 13.229350 * daysSinceJ2000) % 360;
    
    const lon = L + 6.289 * Math.sin(M * Math.PI / 180);
    const lat = 5.128 * Math.sin(F * Math.PI / 180);
    
    const obliquity = 23.439;
    const lonRad = lon * Math.PI / 180;
    const latRad = lat * Math.PI / 180;
    const oblRad = obliquity * Math.PI / 180;
    
    const ra = Math.atan2(
      Math.sin(lonRad) * Math.cos(oblRad) - Math.tan(latRad) * Math.sin(oblRad),
      Math.cos(lonRad)
    ) * 180 / Math.PI;
    
    const dec = Math.asin(
      Math.sin(latRad) * Math.cos(oblRad) + 
      Math.cos(latRad) * Math.sin(oblRad) * Math.sin(lonRad)
    ) * 180 / Math.PI;
    
    const hour = date.getHours() + date.getMinutes() / 60;
    const lst = (100.46 + 0.985647 * daysSinceJ2000 + longitude + hour * 15) % 360;
    const ha = lst - ra;
    
    const latRadObs = latitude * Math.PI / 180;
    const decRad = dec * Math.PI / 180;
    const haRad = ha * Math.PI / 180;
    
    const sinAlt = Math.sin(latRadObs) * Math.sin(decRad) + 
                   Math.cos(latRadObs) * Math.cos(decRad) * Math.cos(haRad);
    const altitude = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * 180 / Math.PI;
    
    const cosAz = (Math.sin(decRad) - Math.sin(latRadObs) * Math.sin(altitude * Math.PI / 180)) / 
                  (Math.cos(latRadObs) * Math.cos(altitude * Math.PI / 180) + 0.0001);
    let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * 180 / Math.PI;
    if (Math.sin(haRad) > 0) azimuth = 360 - azimuth;
    
    return { azimuth, altitude };
  }, [latitude, longitude]);

  // Request device orientation permission (iOS 13+)
  const requestOrientationPermission = async () => {
    if (typeof DeviceOrientationEvent !== 'undefined' && 
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        setHasOrientationPermission(permission === 'granted');
      } catch (e) {
        console.error('Orientation permission error:', e);
        setUseManualHeading(true);
      }
    } else {
      setHasOrientationPermission(true);
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
        setCameraError(null);
      }
      
      await requestOrientationPermission();
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.message || t('ar.cameraErrorDefault'));
      setCameraActive(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  // Handle device orientation
  useEffect(() => {
    const handleOrientation = (event) => {
      if (event.alpha !== null) {
        setDeviceOrientation({
          alpha: event.alpha || 0,
          beta: event.beta || 0,
          gamma: event.gamma || 0
        });
        
        // Calculate compass heading
        let heading = event.alpha;
        if (event.webkitCompassHeading) {
          heading = event.webkitCompassHeading;
        } else if (event.alpha) {
          heading = 360 - event.alpha;
        }
        setCompassHeading(heading);
      }
    };

    if (cameraActive && hasOrientationPermission) {
      window.addEventListener('deviceorientation', handleOrientation, true);
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [cameraActive, hasOrientationPermission]);

  // Draw AR overlay
  useEffect(() => {
    if (!cameraActive) return;
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    
    const drawOverlay = () => {
      const width = canvas.width;
      const height = canvas.height;
      
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
      
      // Get current heading
      const heading = useManualHeading ? manualHeading : compassHeading;
      
      // Calculate positions with time offset
      const now = new Date();
      now.setHours(now.getHours() + timeOffset);
      const sunPos = calculateSunPosition(now);
      const moonPos = calculateMoonPosition(now);
      
      // Calculate screen positions based on heading and tilt
      const fovH = 60; // Approximate camera FOV
      const fovV = 45;
      
      // Sun position on screen
      let sunAzDiff = sunPos.azimuth - heading;
      if (sunAzDiff > 180) sunAzDiff -= 360;
      if (sunAzDiff < -180) sunAzDiff += 360;
      
      const sunX = width / 2 + (sunAzDiff / fovH) * width;
      const sunY = height / 2 - (sunPos.altitude / fovV) * height;
      
      // Moon position on screen
      let moonAzDiff = moonPos.azimuth - heading;
      if (moonAzDiff > 180) moonAzDiff -= 360;
      if (moonAzDiff < -180) moonAzDiff += 360;
      
      const moonX = width / 2 + (moonAzDiff / fovH) * width;
      const moonY = height / 2 - (moonPos.altitude / fovV) * height;
      
      // Draw horizon line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw compass directions
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      
      const directions = [
        { name: 'N', az: 0 },
        { name: 'NE', az: 45 },
        { name: 'E', az: 90 },
        { name: 'SE', az: 135 },
        { name: 'S', az: 180 },
        { name: 'SW', az: 225 },
        { name: 'W', az: 270 },
        { name: 'NW', az: 315 }
      ];
      
      directions.forEach(dir => {
        let azDiff = dir.az - heading;
        if (azDiff > 180) azDiff -= 360;
        if (azDiff < -180) azDiff += 360;
        
        if (Math.abs(azDiff) < fovH / 2 + 10) {
          const x = width / 2 + (azDiff / fovH) * width;
          ctx.fillText(dir.name, x, height / 2 + 20);
          
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.beginPath();
          ctx.moveTo(x, height / 2 - 10);
          ctx.lineTo(x, height / 2 + 5);
          ctx.stroke();
        }
      });
      
      // Draw sun if visible on screen
      if (sunX > -50 && sunX < width + 50 && sunY > -50 && sunY < height + 50) {
        // Sun glow
        const sunGradient = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 60);
        sunGradient.addColorStop(0, 'rgba(255, 200, 50, 0.8)');
        sunGradient.addColorStop(0.3, 'rgba(255, 150, 50, 0.4)');
        sunGradient.addColorStop(1, 'rgba(255, 100, 50, 0)');
        ctx.fillStyle = sunGradient;
        ctx.beginPath();
        ctx.arc(sunX, sunY, 60, 0, Math.PI * 2);
        ctx.fill();
        
        // Sun core
        ctx.fillStyle = '#FFD700';
        ctx.beginPath();
        ctx.arc(sunX, sunY, 20, 0, Math.PI * 2);
        ctx.fill();
        
        // Sun icon
        ctx.fillStyle = '#000';
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('☀', sunX, sunY + 5);
        
        // Sun label
        ctx.fillStyle = '#FFD700';
        ctx.font = 'bold 12px sans-serif';
        ctx.fillText(`${t('ar.sun')} ${sunPos.altitude.toFixed(0)}°`, sunX, sunY + 40);
      }
      
      // Draw moon if visible on screen
      if (moonX > -50 && moonX < width + 50 && moonY > -50 && moonY < height + 50) {
        // Moon glow
        const moonGradient = ctx.createRadialGradient(moonX, moonY, 0, moonX, moonY, 40);
        moonGradient.addColorStop(0, 'rgba(200, 200, 255, 0.6)');
        moonGradient.addColorStop(0.5, 'rgba(150, 150, 200, 0.3)');
        moonGradient.addColorStop(1, 'rgba(100, 100, 150, 0)');
        ctx.fillStyle = moonGradient;
        ctx.beginPath();
        ctx.arc(moonX, moonY, 40, 0, Math.PI * 2);
        ctx.fill();
        
        // Moon core
        ctx.fillStyle = '#E0E0F0';
        ctx.beginPath();
        ctx.arc(moonX, moonY, 15, 0, Math.PI * 2);
        ctx.fill();
        
        // Moon icon
        ctx.fillStyle = '#333';
        ctx.font = '14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('🌙', moonX, moonY + 4);
        
        // Moon label
        ctx.fillStyle = '#E0E0F0';
        ctx.font = 'bold 12px sans-serif';
        ctx.fillText(`${t('ar.moon')} ${moonPos.altitude.toFixed(0)}°`, moonX, moonY + 35);
      }
      
      // Draw crosshair at center
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(width / 2 - 20, height / 2);
      ctx.lineTo(width / 2 - 5, height / 2);
      ctx.moveTo(width / 2 + 5, height / 2);
      ctx.lineTo(width / 2 + 20, height / 2);
      ctx.moveTo(width / 2, height / 2 - 20);
      ctx.lineTo(width / 2, height / 2 - 5);
      ctx.moveTo(width / 2, height / 2 + 5);
      ctx.lineTo(width / 2, height / 2 + 20);
      ctx.stroke();
      
      // Info overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(10, 10, 160, 70);
      ctx.fillStyle = 'white';
      ctx.font = '11px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`${t('ar.heading')}: ${heading.toFixed(0)}°`, 20, 28);
      ctx.fillText(`${t('ar.sun')}: Az ${sunPos.azimuth.toFixed(0)}° Alt ${sunPos.altitude.toFixed(0)}°`, 20, 44);
      ctx.fillText(`${t('ar.moon')}: Az ${moonPos.azimuth.toFixed(0)}° Alt ${moonPos.altitude.toFixed(0)}°`, 20, 60);

      if (timeOffset !== 0) {
        ctx.fillStyle = '#f59e0b';
        ctx.fillText(`${t('ar.time')}: ${timeOffset > 0 ? '+' : ''}${timeOffset}h`, 20, 76);
      }
      
      animationRef.current = requestAnimationFrame(drawOverlay);
    };
    
    drawOverlay();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [cameraActive, compassHeading, manualHeading, useManualHeading, timeOffset, calculateSunPosition, calculateMoonPosition, t]);

  // Get current sun/moon positions for display
  const now = new Date();
  now.setHours(now.getHours() + timeOffset);
  const sunPos = calculateSunPosition(now);
  const moonPos = calculateMoonPosition(now);

  return (
    <div className="space-y-4">
      {/* AR View */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Camera className="w-4 h-4 text-cyan-400" />
              {t('ar.title')}
            </CardTitle>
            {cameraActive && (
              <Badge className="bg-red-500/20 text-red-300 animate-pulse">
                {t('ar.live')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Camera view */}
          <div className="relative rounded-lg overflow-hidden border border-white/10 bg-black" style={{ height: '280px' }}>
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              muted
            />
            <canvas
              ref={canvasRef}
              width={640}
              height={360}
              className="absolute inset-0 w-full h-full"
            />
            
            {!cameraActive && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90">
                {cameraError ? (
                  <>
                    <AlertTriangle className="w-12 h-12 text-amber-500 mb-2" />
                    <p className="text-sm text-amber-400 mb-2">{cameraError}</p>
                    <p className="text-xs text-slate-500 mb-4 text-center px-4">
                      {t('ar.httpsHint')}
                    </p>
                  </>
                ) : (
                  <>
                    <Smartphone className="w-12 h-12 text-slate-500 mb-2" />
                    <p className="text-sm text-slate-400 mb-2">{t('ar.title')}</p>
                    <p className="text-xs text-slate-500 mb-4 text-center px-4">
                      {t('ar.pointCameraHint')}
                    </p>
                  </>
                )}
                <Button
                  onClick={startCamera}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  <Video className="w-4 h-4 mr-2" />
                  {t('ar.startCamera')}
                </Button>
              </div>
            )}
          </div>

          {/* Controls when camera active */}
          {cameraActive && (
            <>
              {/* Time offset */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-400 flex items-center gap-2">
                  <Sun className="w-3 h-3" />
                  {t('ar.timeOffset')}: {timeOffset > 0 ? '+' : ''}{timeOffset}h
                </Label>
                <Slider
                  value={[timeOffset]}
                  onValueChange={([v]) => setTimeOffset(v)}
                  min={-12}
                  max={12}
                  step={0.5}
                />
              </div>

              {/* Manual heading if no compass */}
              {useManualHeading && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-400 flex items-center gap-2">
                    <Compass className="w-3 h-3" />
                    {t('ar.manualHeading')}: {manualHeading}°
                  </Label>
                  <Slider
                    value={[manualHeading]}
                    onValueChange={([v]) => setManualHeading(v)}
                    min={0}
                    max={360}
                    step={5}
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={stopCamera}
                  className="flex-1 border-white/10"
                >
                  <VideoOff className="w-4 h-4 mr-1" />
                  {t('ar.stopCamera')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTimeOffset(0)}
                  className="border-white/10"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUseManualHeading(!useManualHeading)}
                  className="border-white/10"
                >
                  <Compass className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Current Positions */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-500/10 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <Sun className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-400">{t('ar.sun')}</span>
              </div>
              <p className="text-xs text-slate-400">{t('ar.azimuth')}: <span className="text-white">{sunPos.azimuth.toFixed(1)}°</span></p>
              <p className="text-xs text-slate-400">{t('ar.altitude')}: <span className={sunPos.altitude > 0 ? 'text-amber-400' : 'text-blue-400'}>{sunPos.altitude.toFixed(1)}°</span></p>
            </div>
            <div className="bg-blue-500/10 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <Moon className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-400">{t('ar.moon')}</span>
              </div>
              <p className="text-xs text-slate-400">{t('ar.azimuth')}: <span className="text-white">{moonPos.azimuth.toFixed(1)}°</span></p>
              <p className="text-xs text-slate-400">{t('ar.altitude')}: <span className={moonPos.altitude > 0 ? 'text-blue-400' : 'text-slate-500'}>{moonPos.altitude.toFixed(1)}°</span></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-3 text-xs text-slate-400 space-y-1">
          <p>📱 <strong>{t('ar.tipMobileTitle')}</strong> {t('ar.tipMobileText')}</p>
          <p>🔄 <strong>{t('ar.tipCalibrateTitle')}</strong> {t('ar.tipCalibrateText')}</p>
          <p>⏰ <strong>{t('ar.tipTimeScrubTitle')}</strong> {t('ar.tipTimeScrubText')}</p>
          <p>🌅 <strong>{t('ar.tipUseCaseTitle')}</strong> {t('ar.tipUseCaseText')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ARCameraView;
